import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Table, Column, Cell } from 'fixed-data-table-2';
import withStyles from 'isomorphic-style-loader/withStyles';
import { connect } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';

import Checkbox from '../../ui/Checkbox';
import { setSelectedVariantAndUpdateRange } from '../../../actions/GenomeActions';
import { getTextWidth } from '../../../core/doc_util';
import Filter from '../../Filter';
import s from './VariantScatterTable.css';

const FA_SORT_ICON = 30; // width and height of FontAwesome sort icon plus necessary space

const ROW_HEIGHT = 40;
const TABLE_CELL_PADDING = 8;
const MAX_TABLE_HEIGHT = 400;
const SCROLLBAR_WIDTH = 16;
const TABLE_HEADER_HEIGHT = 54;
const ROW_COLOR = {
  selected: '#88AAFF',
  inRange: '#f6f6f6',
};

const FilteredCell = ({
  data,
  rowIndex,
  columnKey,
  inRangeData,
  selectedData,
  ...props
}) => {
  const output = data[rowIndex][columnKey];
  let style = {};
  if (selectedData.idx === data[rowIndex].idx) {
    style = { backgroundColor: ROW_COLOR.selected };
  } else if (
    inRangeData.filter(included => included.idx === data[rowIndex].idx).length >
    0
  ) {
    style = { backgroundColor: ROW_COLOR.inRange };
  }
  return (
    <div style={style}>
      <Cell {...props}>{output}</Cell>
    </div>
  );
};

FilteredCell.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  inRangeData: PropTypes.arrayOf(PropTypes.object),
  rowIndex: PropTypes.number,
  columnKey: PropTypes.string,
  selectedData: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ),
};

FilteredCell.defaultProps = {
  data: [],
  inRangeData: [],
  rowIndex: null,
  columnKey: null,
  selectedData: {},
};

const VariantTable = ({
  width,
  height,
  data,
  selectedCol,
  selectedData,
  onColSelect,
  colDirection,
  scoreList,
  inRangeData,
  genomeRange,
  dispatch,
}) => {
  const selectCol = col => {
    onColSelect(data, col);
  };
  const rowClicked = (e, rowIndex) => {
    const variant = data[rowIndex];
    dispatch(setSelectedVariantAndUpdateRange(variant, genomeRange));
  };
  const rowClassName = () => s.row;
  const useableWidth =
    height === MAX_TABLE_HEIGHT ? width - SCROLLBAR_WIDTH : width;
  let remainingWidth = useableWidth;

  const scoreColumns = scoreList
    ? scoreList.map(score => (
        <Column
          header={
            <Cell
              className={s.link}
              onClick={() => selectCol(score.short_name)}
              style={{ background: 'rgba(56, 165, 226, 0.5)' }}
            >
              {score.name}{' '}
              {selectedCol === score.short_name && (
                <i
                  className={
                    colDirection === 'max'
                      ? 'fa fa-sort-amount-desc'
                      : 'fa fa-sort-amount-asc'
                  }
                />
              )}
            </Cell>
          }
          width={(() => {
            let scoreWidth = getTextWidth(`${score.name}`, {
              fontWeight: 700,
            });
            scoreWidth += FA_SORT_ICON + TABLE_CELL_PADDING * 2;
            remainingWidth -= scoreWidth;
            return scoreWidth;
          })()}
          columnKey={score.short_name}
          key={score.short_name}
          cell={
            <FilteredCell
              data={data}
              inRangeData={inRangeData}
              selectedData={selectedData}
              key={score.short_name}
              style={{ background: 'rgba(56, 165, 226, 0.15)' }}
            />
          }
        />
      ))
    : [];

  return (
    <Table
      className={s.table}
      rowHeight={ROW_HEIGHT}
      rowsCount={data.length}
      width={width}
      height={height}
      headerHeight={TABLE_HEADER_HEIGHT}
      onRowClick={rowClicked}
      rowClassNameGetter={rowClassName}
      onWheel={false}
      showScrollbarX={false}
    >
      <Column
        header={<Cell>Chromosome</Cell>}
        width={remainingWidth / 5}
        columnKey="chromosome"
        key="chromosome"
        cell={
          <FilteredCell
            data={data}
            inRangeData={inRangeData}
            selectedData={selectedData}
          />
        }
      />
      <Column
        header={<Cell>Position</Cell>}
        width={remainingWidth / 5}
        columnKey="position"
        key="position"
        cell={
          <FilteredCell
            data={data}
            inRangeData={inRangeData}
            selectedData={selectedData}
          />
        }
      />
      <Column
        header={<Cell>Name</Cell>}
        width={remainingWidth / 5}
        columnKey="name"
        key="name"
        cell={
          <FilteredCell
            data={data}
            inRangeData={inRangeData}
            selectedData={selectedData}
          />
        }
      />
      <Column
        header={<Cell>Ref Allele</Cell>}
        width={remainingWidth / 5}
        columnKey="ref_allele"
        key="ref_allele"
        cell={
          <FilteredCell
            data={data}
            inRangeData={inRangeData}
            selectedData={selectedData}
          />
        }
      />
      <Column
        header={<Cell>Alt Allele</Cell>}
        width={remainingWidth / 5}
        columnKey="alt_allele"
        key="alt_allele"
        cell={
          <FilteredCell
            data={data}
            inRangeData={inRangeData}
            selectedData={selectedData}
          />
        }
      />
      {scoreColumns}
    </Table>
  );
};

VariantTable.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  inRangeData: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedCol: PropTypes.string,
  colDirection: PropTypes.string.isRequired,
  onColSelect: PropTypes.func.isRequired,
  scoreList: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedData: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ),
  selectedVariant: PropTypes.shape({
    position: PropTypes.number,
    chromosome: PropTypes.string,
    ref_allele: PropTypes.string,
    alt_allele: PropTypes.string,
  }),
  genomeRange: PropTypes.shape({
    startPosition: PropTypes.number,
    endPosition: PropTypes.number,
  }),
  dispatch: PropTypes.func.isRequired,
};

VariantTable.defaultProps = {
  selectedCol: '',
  selectedData: {},
  selectedVariant: null,
  genomeRange: null,
};

const FilteredVariantTable = Filter({
  dataField: 'data',
  fields: ['chromosome', 'name', 'position'],
})(VariantTable);

class VariantScatterTable extends Component {
  static propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    startPosition: PropTypes.number.isRequired,
    endPosition: PropTypes.number.isRequired,
    chromosome: PropTypes.string.isRequired,
    selectedVariant: PropTypes.shape({
      position: PropTypes.number,
      chromosome: PropTypes.string,
      ref_allele: PropTypes.string,
      alt_allele: PropTypes.string,
    }).isRequired,
    dispatch: PropTypes.func.isRequired,
    scoreList: PropTypes.arrayOf(PropTypes.object),
  };

  static defaultProps = {
    scoreList: [],
  };

  constructor(props) {
    super(props);
    this.state = {
      inRangeData: [],
      selectedCol: '',
      colDirection: 'max',
      selectedData: {},
      inRangeDataSearch: false,
    };
    this.sortedAndFilteredData = [];
    this.timeout = null;
    this.positionChanging = false;
    this.tableRef = React.createRef();
  }

  componentDidMount() {
    const { data, scoreList } = this.props;
    const { colDirection } = this.state;

    // Chrome issue - prevent page from scrolling when scrolling table
    this.tableRef.current.addEventListener('wheel', this.onScroll);
    this.sortedAndFilteredData = this.sortData(
      data,
      scoreList[0].short_name,
      colDirection,
    );
    this.updateIncludedData(scoreList[0].short_name);
  }

  shouldComponentUpdate() {
    return !this.positionChanging;
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      data,
      startPosition,
      endPosition,
      scoreList,
      chromosome,
      selectedVariant,
    } = this.props;
    const { colDirection } = this.state;
    const colName =
      prevState.selectedCol === ''
        ? scoreList[0].short_name
        : prevState.selectedCol;
    if (prevProps.data !== data) {
      this.sortedAndFilteredData = this.sortData(data, colName, colDirection);
      this.updateIncludedData(colName);
    }
    if (
      prevProps.startPosition !== startPosition ||
      prevProps.endPosition !== endPosition ||
      prevProps.chromosome !== chromosome ||
      prevProps.selectedVariant !== selectedVariant
    ) {
      this.positionChanging = true;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => this.updateIncludedData(colName), 500);
    }
  }

  componentWillUnmount() {
    this.tableRef.current.removeEventListener('wheel', this.onScroll);
  }

  onScroll = event => {
    event.preventDefault();
  };

  onColSelect = (data, col) => {
    const { colDirection, selectedCol } = this.state;
    let newDir = colDirection;
    if (selectedCol === col) {
      newDir = newDir === 'max' ? 'min' : 'max';
    } else {
      newDir = 'max';
    }
    this.sortedAndFilteredData = this.sortData(data, col, newDir);
    this.setState({ selectedCol: col, colDirection: newDir });
  };

  sortData = (data, col, dir) => {
    const sortedData = data.sort((a, b) => {
      if (dir === 'min') {
        if (Number.isNaN(a[col])) {
          return -1;
        }
        if (Number.isNaN(b[col])) {
          return 1;
        }
        return a[col] - b[col];
      }
      if (Number.isNaN(b[col])) {
        return -1;
      }
      if (Number.isNaN(a[col])) {
        return 1;
      }
      return b[col] - a[col];
    });
    return sortedData;
  };

  updateIncludedData(nameSelection) {
    const {
      startPosition,
      endPosition,
      chromosome,
      selectedVariant,
    } = this.props;
    this.positionChanging = false;
    const inRangeData = this.sortedAndFilteredData.filter(
      variant =>
        variant.position <= endPosition &&
        variant.position >= startPosition &&
        variant.chromosome === chromosome,
    );
    const selectedData = this.sortedAndFilteredData.find(
      variant =>
        variant.position === selectedVariant.position &&
        variant.chromosome === selectedVariant.chromosome &&
        variant.ref_allele === selectedVariant.ref_allele &&
        variant.alt_allele === selectedVariant.alt_allele,
    );

    this.setState({ inRangeData, selectedCol: nameSelection, selectedData });
  }

  render() {
    const { dispatch, scoreList, startPosition, endPosition } = this.props;
    const {
      inRangeDataSearch,
      inRangeData,
      selectedData,
      colDirection,
      selectedCol,
    } = this.state;
    const tableData = inRangeDataSearch
      ? inRangeData
      : this.sortedAndFilteredData;

    const selectOptions = [
      {
        label: 'Chr',
        value: 'chromosome',
      },
      {
        label: 'Position',
        value: 'position',
      },
      {
        label: 'Name',
        value: 'name',
      },
    ];

    const genomeRange = { startPosition, endPosition };

    const calculatedHeight = Math.min(
      TABLE_HEADER_HEIGHT + 2 + tableData.length * ROW_HEIGHT,
      MAX_TABLE_HEIGHT,
    );
    return (
      <div className={s.container} ref={this.tableRef}>
        <div>
          <Checkbox
            key="isInRange"
            checked={inRangeDataSearch}
            onChange={() => {
              this.setState({
                inRangeDataSearch: !inRangeDataSearch,
              });
            }}
          >
            Show data in genomic range
          </Checkbox>
        </div>
        <div style={{ width: '100%', height: `${calculatedHeight}px` }}>
          <AutoSizer>
            {({ width }) => (
              <FilteredVariantTable
                width={width}
                height={calculatedHeight}
                data={tableData}
                inRangeData={inRangeData}
                colDirection={colDirection}
                selectedCol={selectedCol}
                onColSelect={this.onColSelect}
                dispatch={dispatch}
                scoreList={scoreList}
                selectedData={selectedData}
                filterSelect
                selectOptions={selectOptions}
                genomeRange={genomeRange}
              />
            )}
          </AutoSizer>
        </div>
      </div>
    );
  }
}

function select(state) {
  return {
    startPosition: state.genome.startPosition,
    endPosition: state.genome.endPosition,
    chromosome: state.genome.chromosome,
    selectedVariant: state.genome.selectedVariant,
  };
}

export default connect(select)(withStyles(s)(VariantScatterTable));
